import React from 'react';
import { ME_ID } from './data.jsx';
// tasks.jsx - To-Do List view embedded in the IT Helpdesk
  const { useState, useEffect, useRef } = React;

  // Shared API caller - see window.IT_AUTH.api in index.html.
  const taskApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'taskApi');

  // True on phone-sized screens. Drives the phone-first Tasks view below;
  // desktop/tablet keep the kanban dashboards unchanged.
  const useTaskIsPhone = () => {
    const q = '(max-width: 600px)';
    const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
    useEffect(() => {
      if (!window.matchMedia) return undefined;
      const mq = window.matchMedia(q);
      const on = () => setPhone(mq.matches);
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }, []);
    return phone;
  };

  // ── Sticky filter state - now actually sticky ─────────────────────────────────
  // These used to be module-level `let` vars that only survived within a
  // single browser tab session - a hard-refresh reloaded the JS module
  // and every filter reset. Now backed by localStorage so a Pending-only
  // view stays a Pending-only view across refreshes.
  const _STICKY_KEY = 'triage:tasks-filters-v1';
  const _stickyDefaults = {
    view: 'board', memberView: 'kanban', calView: 'month',
    assignee: 'all', priority: 'all',
    showPending: true, showInProgress: true, showCompleted: true, showOverdue: true,
  };
  const _stickyLoad = () => {
    try {
      const raw = localStorage.getItem(_STICKY_KEY);
      return raw ? { ..._stickyDefaults, ...JSON.parse(raw) } : { ..._stickyDefaults };
    } catch (_) { return { ..._stickyDefaults }; }
  };
  const _stickyState = _stickyLoad();
  const _stickySave = () => {
    try { localStorage.setItem(_STICKY_KEY, JSON.stringify(_stickyState)); }
    catch (_) { /* private mode / quota - non-fatal */ }
  };
  // Getters + setters shaped like the previous simple vars so the existing
  // callsites keep working - they read _stickyView, write _stickyView = v.
  // We proxy through the state object + a save() on every write.
  let _stickyView          = _stickyState.view;
  let _stickyMemberView    = _stickyState.memberView;
  let _stickyCalView       = _stickyState.calView;
  let _stickyAssignee      = _stickyState.assignee;
  let _stickyPriority      = _stickyState.priority;
  let _stickyShowPending    = _stickyState.showPending;
  let _stickyShowInProgress = _stickyState.showInProgress;
  let _stickyShowCompleted  = _stickyState.showCompleted;
  let _stickyShowOverdue    = _stickyState.showOverdue;
  // Called after every existing "_stickyX = v" assignment to persist the
  // whole set. Cheaper than proxying getters/setters, and matches how the
  // old callsites already work.
  const _stickyPersist = () => {
    _stickyState.view           = _stickyView;
    _stickyState.memberView     = _stickyMemberView;
    _stickyState.calView        = _stickyCalView;
    _stickyState.assignee       = _stickyAssignee;
    _stickyState.priority       = _stickyPriority;
    _stickyState.showPending    = _stickyShowPending;
    _stickyState.showInProgress = _stickyShowInProgress;
    _stickyState.showCompleted  = _stickyShowCompleted;
    _stickyState.showOverdue    = _stickyShowOverdue;
    _stickySave();
  };

  // ── Data ─────────────────────────────────────────────────────────────────────
  const TASK_USERS = [
    { id:'alex',  name:'Alex',  fullName:'Alex Mullin',     role:'IT Helpdesk Assistant', isManager:false, initials:'AM', color:'#3B82F6' },
    { id:'ed',    name:'Ed',    fullName:'Ed Osagie',        role:'IT Systems Analyst',    isManager:false, initials:'EO', color:'#8B5CF6' },
    { id:'fiona', name:'Fiona', fullName:'Fiona Norman',     role:'Training Manager',      isManager:false, initials:'FN', color:'#EC4899' },
    { id:'james', name:'James', fullName:'James Clydesdale', role:'IT Manager',            isManager:true,  initials:'JC', color:'#0E9384' },
    { id:'rob',   name:'Rob',   fullName:'Rob Hilton',       role:'Head of IT',            isManager:true,  initials:'RH', color:'#F59E0B' },
  ];
  const TASK_MANAGERS = TASK_USERS.filter(u => u.isManager);
  const TASK_MEMBERS  = TASK_USERS.filter(u => !u.isManager);
  const TASK_CATS = ['Hardware','Software','Network','Security','Helpdesk','User Support','Infrastructure','Licensing'];
  const TASK_PRIS = ['Low','Medium','High','Critical'];
  const TASK_PRI_CFG = {
    Low:      { color:'#22C55E', bg:'#F0FDF4' },
    Medium:   { color:'#F59E0B', bg:'#FFFBEB' },
    High:     { color:'#F97316', bg:'#FFF7ED' },
    Critical: { color:'#EF4444', bg:'#FEF2F2' },
  };
  const TASK_STA_CFG = {
    'pending':     { color:'#64748B', bg:'#F1F5F9', label:'Pending' },
    'in-progress': { color:'#3B82F6', bg:'#EFF6FF', label:'In Progress' },
    'completed':   { color:'#22C55E', bg:'#F0FDF4', label:'Completed' },
  };
  const PRI_COLOR = { Critical:'#EF4444', High:'#F97316', Medium:'#F59E0B', Low:'#22C55E' };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  function taskFmtDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
    return d.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
  }
  function taskFmtRelative(isoStr) {
    const diff = Date.now() - new Date(isoStr).getTime();
    if (diff < 60000)    return 'just now';
    if (diff < 3600000)  return Math.floor(diff/60000)+'m ago';
    if (diff < 86400000) return Math.floor(diff/3600000)+'h ago';
    return Math.floor(diff/86400000)+'d ago';
  }
  function taskDueSt(dueDate, status) {
    if (status === 'completed') return 'done';
    if (!dueDate) return 'ok';
    const diff = Math.ceil((new Date(dueDate+'T23:59:59') - Date.now()) / 86400000);
    if (diff < 0)  return 'overdue';
    if (diff === 0) return 'today';
    if (diff <= 2)  return 'soon';
    return 'ok';
  }
  const isOverdueTask = t => t.status !== 'completed' && t.dueDate && new Date(t.dueDate+'T23:59:59') < new Date();
  const toDateKey = d => d.toISOString().split('T')[0];
  // Today's date in YYYY-MM-DD using LOCAL time (not UTC). Used as `min`
  // on due-date inputs so users can't pick a date in the past. toISOString
  // would give UTC, which is off-by-a-day near midnight in BST - getDate()
  // here ensures the picker matches what the user sees on their wall clock.
  const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };
  // Parse assignedTo - stored as comma-separated string, e.g. "alex,james"
  const parseAssignees = s => (!s ? [] : s.split(',').map(x => x.trim()).filter(Boolean));

  // ── Shared UI ─────────────────────────────────────────────────────────────────
  function TaskAvatar({ userId, size = 32 }) {
    const u = TASK_USERS.find(u => u.id === userId);
    if (!u) return null;
    return (
      <div style={{ width:size, height:size, borderRadius:'50%', background:u.color, color:'#fff',
        flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:size*0.34, fontWeight:700, letterSpacing:'-0.3px' }}>{u.initials}</div>
    );
  }
  function TaskAvatarGroup({ assignedTo, size = 32 }) {
    const ids = parseAssignees(assignedTo);
    if (ids.length === 0) return null;
    if (ids.length === 1) return <TaskAvatar userId={ids[0]} size={size}/>;
    const shown = ids.slice(0, 3);
    const overlap = Math.floor(size * 0.3);
    return (
      <div style={{ display:'flex', alignItems:'center' }}>
        {shown.map((id, i) => {
          const u = TASK_USERS.find(u => u.id === id);
          if (!u) return null;
          return (
            <div key={id} title={u.fullName}
              style={{ width:size, height:size, borderRadius:'50%', background:u.color, color:'#fff',
                flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:size*0.34, fontWeight:700, letterSpacing:'-0.3px',
                marginLeft: i > 0 ? -overlap : 0,
                border:'2px solid #fff', position:'relative', zIndex: shown.length - i }}>{u.initials}</div>
          );
        })}
        {ids.length > 3 && (
          <div style={{ width:size, height:size, borderRadius:'50%', background:'#94A3B8', color:'#fff',
            flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:size*0.34, fontWeight:700,
            marginLeft: -overlap, border:'2px solid #fff' }}>+{ids.length-3}</div>
        )}
      </div>
    );
  }
  function AssigneeMultiSelect({ selected, currentUserId, onChange }) {
    const toggle = id => {
      if (selected.includes(id)) {
        if (selected.length === 1) return; // must keep at least one assignee
        onChange(selected.filter(x => x !== id));
      } else {
        onChange([...selected, id]);
      }
    };
    return (
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {TASK_USERS.map(u => {
          const active = selected.includes(u.id);
          return (
            <button key={u.id} type="button" onClick={() => toggle(u.id)}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:20,
                border:`1.5px solid ${active ? u.color : '#E2E8F0'}`,
                background: active ? u.color+'18' : '#fff',
                cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600,
                color: active ? u.color : '#64748B', transition:'all 0.12s' }}>
              <div style={{ width:16, height:16, borderRadius:'50%', background:u.color, color:'#fff',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:7.5, fontWeight:700, flexShrink:0 }}>
                {u.initials}
              </div>
              {u.id === currentUserId ? `${u.name} (me)` : u.name}
              {active && <span style={{ marginLeft:1, fontSize:10, opacity:0.8 }}>✓</span>}
            </button>
          );
        })}
      </div>
    );
  }
  function TaskPriBadge({ priority }) {
    const cfg = TASK_PRI_CFG[priority] || TASK_PRI_CFG.Medium;
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600,
        padding:'3px 8px', borderRadius:20, color:cfg.color, background:cfg.bg, whiteSpace:'nowrap' }}>
        <span style={{ width:5, height:5, borderRadius:'50%', background:cfg.color, flexShrink:0 }}/>
        {priority}
      </span>
    );
  }
  function TaskStaBadge({ status }) {
    const cfg = TASK_STA_CFG[status] || TASK_STA_CFG['pending'];
    return (
      <span style={{ display:'inline-flex', alignItems:'center', fontSize:11, fontWeight:600,
        padding:'3px 8px', borderRadius:20, color:cfg.color, background:cfg.bg, whiteSpace:'nowrap' }}>
        {cfg.label}
      </span>
    );
  }
  function TaskCatBadge({ category }) {
    if (!category) return null;
    return (
      <span style={{ fontSize:11, fontWeight:500, padding:'3px 8px', borderRadius:20,
        background:'#F1F5F9', color:'#475569', whiteSpace:'nowrap' }}>{category}</span>
    );
  }

  // ── Status toggles (Pending / In Progress / Completed / Overdue) ──────────────
  function StatusToggles({ showPending, showInProgress, showCompleted, showOverdue, onToggle }) {
    const btns = [
      { key:'pending',     label:'Pending',     color:'#64748B', bg:'#F1F5F9', active:showPending },
      { key:'in-progress', label:'In Progress', color:'#3B82F6', bg:'#EFF6FF', active:showInProgress },
      { key:'completed',   label:'Completed',   color:'#22C55E', bg:'#F0FDF4', active:showCompleted },
      { key:'overdue',     label:'Overdue',     color:'#EF4444', bg:'#FEF2F2', active:showOverdue },
    ];
    return (
      <div style={{ display:'flex', gap:6 }}>
        {btns.map(b => (
          <button key={b.key} onClick={() => onToggle(b.key)}
            style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'6px 12px',
              borderRadius:20, border:'1.5px solid', cursor:'pointer',
              borderColor: b.active ? b.color : '#E2E8F0',
              background:  b.active ? b.bg    : 'transparent',
              color:       b.active ? b.color : '#CBD5E1',
              fontFamily:'inherit', fontSize:12, fontWeight:600, transition:'all 0.12s' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', flexShrink:0,
              background: b.active ? b.color : '#CBD5E1' }}/>
            {b.label}
          </button>
        ))}
      </div>
    );
  }

  // ── Modal shell ───────────────────────────────────────────────────────────────
  function TaskModal({ children, onClose, width = 560 }) {
    useEffect(() => {
      const fn = e => { if (e.key === 'Escape') onClose(); };
      document.addEventListener('keydown', fn);
      return () => document.removeEventListener('keydown', fn);
    }, [onClose]);
    return (
      <div className="tk-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
        <div className="tk-modal" style={{ width, maxWidth:'calc(100vw - 32px)', maxHeight:'calc(100vh - 48px)',
          background:'#fff', borderRadius:14, boxShadow:'0 20px 60px rgba(0,0,0,0.2)',
          display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {children}
        </div>
      </div>
    );
  }

  // ── Toast ─────────────────────────────────────────────────────────────────────
  function TaskToast({ toasts, onDismiss }) {
    const icons  = { assign:'→', complete:'✓', info:'i', delete:'🗑' };
    const colors = { assign:'#0E9384', complete:'#22C55E', info:'#3B82F6', delete:'#94A3B8' };
    return (
      <div style={{ position:'fixed', top:20, right:20, zIndex:9999, display:'flex', flexDirection:'column', gap:10, pointerEvents:'none' }}>
        {toasts.map(t => (
          <div key={t.id} style={{ background:'#0D1F35', color:'#fff', borderRadius:10, padding:'14px 16px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.18)', display:'flex', alignItems:'flex-start', gap:12,
            maxWidth:360, pointerEvents:'auto' }}>
            <div style={{ width:32, height:32, borderRadius:8, flexShrink:0, background:colors[t.type]||'#3B82F6',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:700, color:'#fff' }}>
              {icons[t.type]||'i'}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:2 }}>{t.title}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.65)', lineHeight:1.4 }}>{t.message}</div>
            </div>
            <button onClick={() => onDismiss(t.id)}
              style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.4)', fontSize:18, padding:0, lineHeight:1 }}>×</button>
          </div>
        ))}
      </div>
    );
  }

  // ── Task card ─────────────────────────────────────────────────────────────────
  // Centralised delete-permission rule. Managers (James, Rob) can delete
  // anything. Non-managers (Ed, Alex, Fiona) can only delete tasks where
  // they are the assigner or one of the assignees.
  const userCanDeleteTask = (task, currentUserId) => {
    if (!currentUserId) return false;
    const me = TASK_USERS.find(u => u.id === currentUserId);
    if (me?.isManager) return true;
    if (task.assignedBy === currentUserId) return true;
    return parseAssignees(task.assignedTo).includes(currentUserId);
  };

  function TaskCard({ task, onClick, compact, onDelete, currentUser }) {
    const [hovered, setHovered] = useState(false);
    const assigneeIds   = parseAssignees(task.assignedTo);
    const assigneeNames = assigneeIds.map(id => TASK_USERS.find(u => u.id === id)?.name).filter(Boolean);
    const dueSt    = taskDueSt(task.dueDate, task.status);
    const dueColor = { overdue:'#EF4444', today:'#F97316', soon:'#F59E0B', ok:'#94A3B8', done:'#22C55E' }[dueSt];
    const isDone   = task.status === 'completed';
    const isOD     = dueSt === 'overdue';
    return (
      <div onClick={() => onClick && onClick(task)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ background:'#fff', borderRadius:10,
          padding: compact ? '12px 14px' : '15px 17px',
          boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.08)',
          border: isOD ? '1px solid #FECACA' : '1px solid #E2E8F0',
          borderLeft: isOD ? '3px solid #EF4444' : undefined,
          cursor:'pointer', opacity: isDone ? 0.65 : 1,
          transform: hovered ? 'translateY(-1px)' : 'none', transition:'box-shadow 0.15s, transform 0.12s' }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom: task.description ? 6 : 8 }}>
          <div style={{ flex:1, fontWeight:600, fontSize: compact ? 13 : 14, lineHeight:1.4,
            color: isDone ? '#94A3B8' : isOD ? '#EF4444' : 'var(--text)',
            textDecoration: isDone ? 'line-through' : 'none' }}>
            {task.title}
          </div>
          <TaskPriBadge priority={task.priority}/>
          {onDelete && hovered && userCanDeleteTask(task, currentUser?.id) && (
            <button onClick={e => { e.stopPropagation(); onDelete(task.id); }} title="Move to bin"
              style={{ flexShrink:0, background:'none', border:'none', cursor:'pointer', padding:'2px 4px',
                color:'#94A3B8', borderRadius:4, lineHeight:1 }}
              onMouseEnter={e => e.currentTarget.style.color='#EF4444'}
              onMouseLeave={e => e.currentTarget.style.color='#94A3B8'}>
              <svg viewBox="0 0 20 20" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"/>
              </svg>
            </button>
          )}
        </div>
        {task.description && (
          <div style={{ fontSize:12, color:'#94A3B8', lineHeight:1.45, marginBottom:8,
            display:'-webkit-box', WebkitLineClamp: compact ? 1 : 2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
            {task.description}
          </div>
        )}
        <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 }}>
          <TaskCatBadge category={task.category}/><TaskStaBadge status={task.status}/>
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <TaskAvatarGroup assignedTo={task.assignedTo} size={22}/>
            <span style={{ fontSize:12, color:'#64748B' }}>
              {assigneeNames.length === 0 ? '-' : assigneeNames.length <= 2 ? assigneeNames.join(', ') : `${assigneeNames[0]} +${assigneeNames.length-1}`}
            </span>
          </div>
          {task.dueDate && (
            <span style={{ fontSize:11, fontWeight: dueSt !== 'ok' && dueSt !== 'done' ? 600 : 400, color:dueColor }}>
              {dueSt === 'overdue' ? '! Overdue' : `Due ${taskFmtDate(task.dueDate)}`}
            </span>
          )}
        </div>
        {task.comments?.length > 0 && (
          <div style={{ marginTop:8, fontSize:11, color:'#94A3B8' }}>
            {task.comments.length} comment{task.comments.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    );
  }

  // ── Task detail modal ─────────────────────────────────────────────────────────
  function TaskDetailModal({ task, currentUser, onUpdate, onAddComment, onDelete, onEdit, onClose }) {
    const [local, setLocal]           = useState(task);
    const [comment, setComment]       = useState('');
    const [commenting, setCommenting] = useState(false);
    const [editing, setEditing]       = useState(false);
    const [editForm, setEditForm]     = useState(null);
    const [editSaving, setEditSaving] = useState(false);

    const inp = { border:'1px solid #E2E8F0', borderRadius:8, padding:'9px 12px', fontSize:14,
      background:'#fff', color:'var(--text)', fontFamily:'inherit', outline:'none' };
    const lbl = { display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:'#64748B', marginBottom:6 };

    function startEdit() {
      setEditForm({ title:local.title, description:local.description||'', category:local.category||TASK_CATS[0],
        priority:local.priority, assignees:parseAssignees(local.assignedTo), dueDate:local.dueDate||'', status:local.status,
        convertToTicketOnComplete: !!local.convertToTicketOnComplete });
      setEditing(true);
    }
    const setEF = (k,v) => setEditForm(f => ({...f,[k]:v}));

    async function saveEdit() {
      if (!editForm.title.trim() || editSaving) return;
      // Mirror the same convertToTicketOnComplete warning as the quick-action
      // button. Without this, completing a task via the edit modal's status
      // dropdown silently creates the auto-ticket - inconsistent UX.
      const justCompleting = editForm.status === 'completed' && local.status !== 'completed';
      if (justCompleting && editForm.convertToTicketOnComplete) {
        const assignerName = (TASK_USERS.find(u => u.id === local.assignedBy)?.fullName) || 'the assigner';
        const ok = window.confirm(
          `Heads up: ${assignerName} chose to log this task as a ticket on completion.\n\n` +
          `Marking it completed will automatically create a closed ticket in the helpdesk for the record.\n\n` +
          `Continue?`
        );
        if (!ok) return;
      }
      setEditSaving(true);
      const updates = { ...editForm, assignedTo: editForm.assignees.join(',') };
      try { const saved = await onEdit(local.id, updates); if (saved) setLocal(saved); setEditing(false); }
      catch(_) {}
      setEditSaving(false);
    }

    function changeStatus(s) {
      // If the task has convertToTicketOnComplete set and the user is
      // moving it to completed, warn them so they understand the side
      // effect (a closed ticket will be auto-generated).
      if (s === 'completed' && local.convertToTicketOnComplete) {
        const assignerName = (TASK_USERS.find(u => u.id === local.assignedBy)?.fullName) || 'the assigner';
        const ok = window.confirm(
          `Heads up: ${assignerName} chose to log this task as a ticket on completion.\n\n` +
          `Marking it completed will automatically create a closed ticket in the helpdesk for the record.\n\n` +
          `Continue?`
        );
        if (!ok) return;
      }
      const updated = {...local, status:s, completedAt: s==='completed' ? new Date().toISOString() : null};
      setLocal(updated);
      onUpdate(updated, s==='completed' ? 'completed' : null);
    }
    async function postComment() {
      if (!comment.trim() || commenting) return;
      const text = comment.trim(); setComment(''); setCommenting(true);
      try { const saved = await onAddComment(local.id, {authorId:currentUser.id, text}); if (saved) setLocal(l => ({...l, comments:[...(l.comments||[]), saved]})); }
      catch(e) {}
      setCommenting(false);
    }

    const assigneeIds = parseAssignees(local.assignedTo);
    const assigner    = TASK_USERS.find(u => u.id === local.assignedBy);
    // A user can edit / change status on a task only if they're the
    // assigner OR one of the assignees. Everyone else gets a read-only
    // view (they can still comment).
    const canEdit = currentUser.id === local.assignedBy || assigneeIds.includes(currentUser.id);
    const editInp = { width:'100%', border:'1px solid #E2E8F0', borderRadius:8, padding:'9px 12px', fontSize:14,
      fontFamily:'inherit', outline:'none', background:'#fff', color:'var(--text)', boxSizing:'border-box' };

    if (editing && editForm) {
      const editValid = editForm.title.trim() && !editSaving;
      return (
        <TaskModal onClose={() => setEditing(false)} width={540}>
          <div style={{ padding:'20px 24px', borderBottom:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div><div style={{ fontWeight:700, fontSize:16 }}>Edit Task</div><div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>Update task details</div></div>
            <button onClick={() => setEditing(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#64748B', fontSize:22, lineHeight:1 }}>×</button>
          </div>
          <div style={{ padding:24, overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>
            <div><label style={lbl}>Task Title *</label><input style={editInp} value={editForm.title} onChange={e => setEF('title',e.target.value)}/></div>
            <div><label style={lbl}>Description</label>
              <textarea style={{...editInp, resize:'vertical', minHeight:72, lineHeight:1.6}} value={editForm.description} onChange={e => setEF('description',e.target.value)}/></div>
            <div><label style={lbl}>Assign To</label>
              <AssigneeMultiSelect selected={editForm.assignees} currentUserId={currentUser.id} onChange={v => setEF('assignees',v)}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><label style={lbl}>Category</label>
                <select style={{...editInp, cursor:'pointer'}} value={editForm.category} onChange={e => setEF('category',e.target.value)}>
                  {TASK_CATS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
              <div><label style={lbl}>Due Date</label>
                <input type="date" min={todayKey()} style={{...editInp, cursor:'pointer'}} value={editForm.dueDate} onChange={e => setEF('dueDate',e.target.value)}/></div>
            </div>
            <div><label style={lbl}>Priority</label>
              <div style={{ display:'flex', gap:6 }}>
                {TASK_PRIS.map(p => { const cfg=TASK_PRI_CFG[p]; const active=editForm.priority===p; return (
                  <button key={p} onClick={()=>setEF('priority',p)} style={{ flex:1, padding:'8px 4px', borderRadius:8, cursor:'pointer', border:'1.5px solid',
                    borderColor:active?cfg.color:'#E2E8F0', background:active?cfg.bg:'#fff', color:active?cfg.color:'#64748B', fontFamily:'inherit', fontSize:12, fontWeight:700 }}>{p}</button>
                );})}
              </div></div>
            <div><label style={lbl}>Status</label>
              <select style={{...editInp, cursor:'pointer'}} value={editForm.status} onChange={e => setEF('status',e.target.value)}>
                <option value="pending">Pending</option><option value="in-progress">In Progress</option><option value="completed">Completed</option>
              </select>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', padding:'10px 12px', border:'1px solid #E2E8F0', borderRadius:8, background:editForm.convertToTicketOnComplete?'#F0FDFA':'#fff' }}>
              <input
                type="checkbox"
                checked={editForm.convertToTicketOnComplete}
                onChange={e=>setEF('convertToTicketOnComplete', e.target.checked)}
                style={{ width:16, height:16, accentColor:'#0D9488', cursor:'pointer', flexShrink:0 }}
              />
              <span style={{ fontSize:13, color:'var(--text)' }}>
                <span style={{ fontWeight:600 }}>Convert to ticket once completed</span>
                <span style={{ display:'block', fontSize:11, color:'#64748B', marginTop:2 }}>
                  Logs the completed task as a closed ticket for the record.
                </span>
              </span>
            </label>
          </div>
          <div style={{ padding:'16px 24px', borderTop:'1px solid #E2E8F0', display:'flex', justifyContent:'flex-end', gap:10 }}>
            <button onClick={()=>setEditing(false)} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid #E2E8F0', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:14, color:'#64748B' }}>Cancel</button>
            <button onClick={saveEdit} disabled={!editValid} style={{ padding:'9px 24px', borderRadius:8, border:'none',
              background:editValid?'#0D1F35':'#E2E8F0', color:editValid?'#fff':'#94A3B8',
              cursor:editValid?'pointer':'not-allowed', fontFamily:'inherit', fontSize:14, fontWeight:700 }}>
              {editSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </TaskModal>
      );
    }

    return (
      <TaskModal onClose={onClose} width={580}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E2E8F0', display:'flex', alignItems:'flex-start', gap:12 }}>
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:16, fontWeight:700, lineHeight:1.3, marginBottom:10 }}>{local.title}</h2>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              <TaskPriBadge priority={local.priority}/><TaskStaBadge status={local.status}/><TaskCatBadge category={local.category}/>
              {local.convertToTicketOnComplete && (
                <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:12, background:'#CCFBF1', color:'#0F766E', fontSize:11, fontWeight:600 }}>
                  → Ticket on completion
                </span>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            {canEdit && onEdit && <button onClick={startEdit} style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600, color:'#0D1F35' }}>Edit</button>}
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#64748B', fontSize:22, lineHeight:1 }}>×</button>
          </div>
        </div>
        <div style={{ padding:24, overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:20 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, background:'#F8FAFC', borderRadius:8, padding:16 }}>
            {/* Assigned To - supports multiple */}
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#64748B', marginBottom:5 }}>Assigned To</div>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {assigneeIds.length === 0 ? <span style={{ fontSize:13, fontWeight:600 }}>-</span> : assigneeIds.map(id => {
                  const u = TASK_USERS.find(u => u.id === id);
                  return u ? (
                    <div key={id} style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <TaskAvatar userId={id} size={20}/>
                      <span style={{ fontSize:13, fontWeight:600 }}>{u.fullName}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
            {[
              { label:'Assigned By', value:assigner?.fullName, uid:local.assignedBy },
              { label:'Due Date', value: local.dueDate ? taskFmtDate(local.dueDate) : 'No due date' },
              { label:'Created', value:taskFmtDate(local.createdAt) },
              ...(local.completedAt ? [{label:'Completed', value:taskFmtDate(local.completedAt), green:true}] : []),
            ].map(({label,value,uid,green}) => (
              <div key={label}>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#64748B', marginBottom:5 }}>{label}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {uid && <TaskAvatar userId={uid} size={20}/>}
                  <span style={{ fontSize:13, fontWeight:600, color: green ? '#22C55E' : 'var(--text)' }}>{value}</span>
                </div>
              </div>
            ))}
          </div>
          {local.description && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#64748B', marginBottom:8 }}>Description</div>
              <p style={{ fontSize:14, lineHeight:1.7, color:'var(--text)', background:'#F8FAFC', borderRadius:8, padding:'12px 14px' }}>{local.description}</p>
            </div>
          )}
          {canEdit && local.status !== 'completed' && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#64748B', marginBottom:8 }}>Update Status</div>
              <div style={{ display:'flex', gap:8 }}>
                {['pending','in-progress','completed'].map(s => {
                  const cfg=TASK_STA_CFG[s]; const active=local.status===s;
                  return <button key={s} onClick={()=>changeStatus(s)} style={{ padding:'8px 16px', borderRadius:8, cursor:'pointer', border:'1.5px solid',
                    borderColor:active?cfg.color:'#E2E8F0', background:active?cfg.bg:'#fff', color:active?cfg.color:'#64748B',
                    fontFamily:'inherit', fontSize:13, fontWeight:600 }}>{cfg.label}</button>;
                })}
              </div>
            </div>
          )}
          {!canEdit && (
            <div style={{ fontSize:12, color:'#64748B', fontStyle:'italic', background:'#F8FAFC', borderRadius:8, padding:'10px 14px', border:'1px solid #E2E8F0' }}>
              View-only - you're not the assigner or an assignee. You can still comment below.
            </div>
          )}
          <div>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#64748B', marginBottom:12 }}>
              Comments {local.comments?.length > 0 && `(${local.comments.length})`}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
              {!(local.comments||[]).length && <p style={{ fontSize:13, color:'#94A3B8', fontStyle:'italic' }}>No comments yet.</p>}
              {(local.comments||[]).map(c => {
                const author = TASK_USERS.find(u => u.id === c.authorId);
                return (
                  <div key={c.id} style={{ display:'flex', gap:10 }}>
                    <TaskAvatar userId={c.authorId} size={28}/>
                    <div style={{ flex:1, background:'#F8FAFC', borderRadius:8, padding:'10px 13px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontWeight:700, fontSize:13 }}>{author?.name}</span>
                        <span style={{ fontSize:11, color:'#64748B' }}>{taskFmtRelative(c.createdAt)}</span>
                      </div>
                      <p style={{ fontSize:13, lineHeight:1.55, color:'var(--text)' }}>{c.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <TaskAvatar userId={currentUser.id} size={28}/>
              <input style={{...inp, flex:1, opacity:commenting?0.6:1}}
                placeholder={commenting?'Posting…':'Add a comment…'} value={comment}
                onChange={e=>setComment(e.target.value)} onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&postComment()} disabled={commenting}/>
              <button onClick={postComment} disabled={!comment.trim()||commenting}
                style={{ padding:'9px 16px', borderRadius:8, border:'none',
                  background:comment.trim()&&!commenting?'#0D1F35':'#E2E8F0',
                  color:comment.trim()&&!commenting?'#fff':'#94A3B8',
                  cursor:comment.trim()&&!commenting?'pointer':'not-allowed', fontFamily:'inherit', fontSize:13, fontWeight:600 }}>Post</button>
            </div>
          </div>
          {onDelete && userCanDeleteTask(local, currentUser?.id) && (
            <div style={{ borderTop:'1px solid #F1F5F9', paddingTop:4 }}>
              <button onClick={()=>{onDelete(local.id);onClose();}}
                style={{ display:'flex', alignItems:'center', gap:7, background:'none', border:'none', cursor:'pointer',
                  fontSize:13, color:'#94A3B8', padding:'6px 8px', borderRadius:6, fontFamily:'inherit' }}
                onMouseEnter={e=>{e.currentTarget.style.color='#EF4444';e.currentTarget.style.background='#FEF2F2';}}
                onMouseLeave={e=>{e.currentTarget.style.color='#94A3B8';e.currentTarget.style.background='none';}}>
                <svg viewBox="0 0 20 20" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"/></svg>
                Move to bin
              </button>
            </div>
          )}
        </div>
      </TaskModal>
    );
  }

  // ── Create task modal ─────────────────────────────────────────────────────────
  function CreateTaskModal({ currentUser, onSave, onClose }) {
    const [form, setForm] = useState({ title:'', description:'', category:TASK_CATS[0], priority:'Medium', assignees:[currentUser.id], dueDate:'', convertToTicketOnComplete:false });
    const [saving, setSaving] = useState(false);
    const set = (k,v) => setForm(f=>({...f,[k]:v}));
    const valid = form.title.trim() && form.assignees.length > 0 && !saving;
    const inp = { width:'100%', border:'1px solid #E2E8F0', borderRadius:8, padding:'9px 12px', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff', color:'var(--text)', boxSizing:'border-box' };
    const lbl = { display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:'#64748B', marginBottom:6 };
    async function handleSave() {
      if (!valid) return; setSaving(true);
      await onSave({...form, assignedTo:form.assignees.join(','), assignedBy:currentUser.id, status:'pending', createdAt:new Date().toISOString(), completedAt:null, comments:[]});
      setSaving(false);
    }
    return (
      <TaskModal onClose={onClose} width={540}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div><div style={{ fontWeight:700, fontSize:16 }}>New Task</div><div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>Assign a task to a team member or yourself</div></div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#64748B', fontSize:22, lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:24, overflowY:'auto', display:'flex', flexDirection:'column', gap:18 }}>
          <div><label style={lbl}>Task Title *</label><input style={inp} placeholder="e.g. Replace faulty keyboard in Room 3" value={form.title} onChange={e=>set('title',e.target.value)}/></div>
          <div><label style={lbl}>Description</label><textarea style={{...inp,resize:'vertical',minHeight:80,lineHeight:1.6}} placeholder="Provide any relevant details or steps…" value={form.description} onChange={e=>set('description',e.target.value)}/></div>
          <div><label style={lbl}>Assign To *</label>
            <AssigneeMultiSelect selected={form.assignees} currentUserId={currentUser.id} onChange={v=>set('assignees',v)}/>
          </div>
          {/* Convert to ticket toggle. When checked, completing the task
              auto-creates a closed ticket so the work is logged in the
              ticket history for record-keeping. */}
          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', padding:'10px 12px', border:'1px solid #E2E8F0', borderRadius:8, background:form.convertToTicketOnComplete?'#F0FDFA':'#fff' }}>
            <input
              type="checkbox"
              checked={form.convertToTicketOnComplete}
              onChange={e=>set('convertToTicketOnComplete', e.target.checked)}
              style={{ width:16, height:16, accentColor:'#0D9488', cursor:'pointer', flexShrink:0 }}
            />
            <span style={{ fontSize:13, color:'var(--text)' }}>
              <span style={{ fontWeight:600 }}>Convert to ticket once completed</span>
              <span style={{ display:'block', fontSize:11, color:'#64748B', marginTop:2 }}>
                Logs the completed task as a closed ticket for the record.
              </span>
            </span>
          </label>
          <div><label style={lbl}>Category</label><select style={{...inp,cursor:'pointer'}} value={form.category} onChange={e=>set('category',e.target.value)}>{TASK_CATS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
          <div><label style={lbl}>Priority</label>
            <div style={{ display:'flex', gap:6 }}>
              {TASK_PRIS.map(p=>{const cfg=TASK_PRI_CFG[p];const active=form.priority===p;return(
                <button key={p} onClick={()=>set('priority',p)} style={{ flex:1, padding:'8px 4px', borderRadius:8, cursor:'pointer', border:'1.5px solid', borderColor:active?cfg.color:'#E2E8F0', background:active?cfg.bg:'#fff', color:active?cfg.color:'#64748B', fontFamily:'inherit', fontSize:12, fontWeight:700 }}>{p}</button>
              );})}
            </div></div>
          <div><label style={lbl}>Due Date</label><input type="date" min={todayKey()} style={{...inp,cursor:'pointer',width:'50%'}} value={form.dueDate} onChange={e=>set('dueDate',e.target.value)}/></div>
        </div>
        <div style={{ padding:'16px 24px', borderTop:'1px solid #E2E8F0', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid #E2E8F0', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:14, color:'#64748B' }}>Cancel</button>
          <button onClick={handleSave} disabled={!valid} style={{ padding:'9px 24px', borderRadius:8, border:'none', background:valid?'#0D1F35':'#E2E8F0', color:valid?'#fff':'#94A3B8', cursor:valid?'pointer':'not-allowed', fontFamily:'inherit', fontSize:14, fontWeight:700 }}>{saving?'Saving…':'Create Task'}</button>
        </div>
      </TaskModal>
    );
  }

  // ── Stat card ─────────────────────────────────────────────────────────────────
  function TaskStatCard({ label, value, accent }) {
    return (
      <div style={{ flex:1, background:'#fff', borderRadius:10, padding:'18px 20px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)', border:'1px solid #E2E8F0' }}>
        <div style={{ fontSize:30, fontWeight:700, color:accent||'var(--text)', lineHeight:1, marginBottom:6 }}>{value}</div>
        <div style={{ fontSize:12, color:'#64748B', fontWeight:500 }}>{label}</div>
      </div>
    );
  }

  // ── Board column ──────────────────────────────────────────────────────────────
  function BoardColumn({ user, tasks, onTaskClick, onDeleteTask, currentUser }) {
    const counts = {
      pending:    tasks.filter(t=>t.status==='pending').length,
      inProgress: tasks.filter(t=>t.status==='in-progress').length,
      completed:  tasks.filter(t=>t.status==='completed').length,
      overdue:    tasks.filter(isOverdueTask).length,
    };
    return (
      <div className="tasks-board-col" style={{ minWidth:260, maxWidth:300, flex:'1 1 260px', display:'flex', flexDirection:'column', gap:8 }}>
        <div style={{ background:'#fff', borderRadius:10, padding:'13px 15px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', gap:10 }}>
          <TaskAvatar userId={user.id} size={36}/>
          <div style={{ flex:1, overflow:'hidden' }}>
            <div style={{ fontWeight:700, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user.name}</div>
            <div style={{ fontSize:11, color:'#64748B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user.role}</div>
          </div>
          <div style={{ display:'flex', gap:4, flexShrink:0 }}>
            {counts.overdue    > 0 && <span style={{ fontSize:10, background:'#FEF2F2', color:'#EF4444', padding:'2px 6px', borderRadius:20, fontWeight:700 }}>{counts.overdue}!</span>}
            {counts.pending    > 0 && <span style={{ fontSize:10, background:'#F1F5F9', color:'#64748B', padding:'2px 6px', borderRadius:20, fontWeight:700 }}>{counts.pending}</span>}
            {counts.inProgress > 0 && <span style={{ fontSize:10, background:'#EFF6FF', color:'#3B82F6', padding:'2px 6px', borderRadius:20, fontWeight:700 }}>{counts.inProgress}</span>}
            {counts.completed  > 0 && <span style={{ fontSize:10, background:'#F0FDF4', color:'#22C55E', padding:'2px 6px', borderRadius:20, fontWeight:700 }}>{counts.completed}</span>}
          </div>
        </div>
        {tasks.length === 0
          ? <div style={{ background:'#F8FAFC', border:'1.5px dashed #E2E8F0', borderRadius:10, padding:'22px 16px', textAlign:'center', color:'#94A3B8', fontSize:13 }}>No tasks assigned</div>
          : tasks.map(t => <TaskCard key={t.id} task={t} onClick={onTaskClick} onDelete={onDeleteTask} compact={true} currentUser={currentUser}/>)
        }
      </div>
    );
  }

  // ── Calendar ──────────────────────────────────────────────────────────────────
  function TaskCalendar({ tasks, onTaskClick }) {
    const todayDate = new Date();
    const todayKey  = toDateKey(todayDate);

    const [calView, setCalView] = useState(() => _stickyCalView);
    const [year,  setYear]      = useState(todayDate.getFullYear());
    const [month, setMonth]     = useState(todayDate.getMonth());
    const [weekStart, setWeekStart] = useState(() => {
      const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d;
    });

    const changeCalView = v => { _stickyCalView = v; _stickyPersist(); setCalView(v); };

    // Group tasks by dueDate key
    const byDate = {};
    for (const t of tasks) {
      if (!t.dueDate) continue;
      if (!byDate[t.dueDate]) byDate[t.dueDate] = [];
      byDate[t.dueDate].push(t);
    }

    // Month helpers
    const firstDay    = new Date(year, month, 1);
    const daysInMonth = new Date(year, month+1, 0).getDate();
    const startDow    = firstDay.getDay();
    const isCurrentMonth = year===todayDate.getFullYear() && month===todayDate.getMonth();

    const prevMonth = () => { if (month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
    const nextMonth = () => { if (month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

    // Week helpers
    const weekDays = Array.from({length:7}, (_,i) => new Date(weekStart.getTime() + i*86400000));
    const weekEndKey = toDateKey(weekDays[6]);
    const weekStartKey = toDateKey(weekDays[0]);
    const isCurrentWeek = weekStartKey <= todayKey && todayKey <= weekEndKey;

    const prevWeek = () => setWeekStart(d => new Date(d.getTime() - 7*86400000));
    const nextWeek = () => setWeekStart(d => new Date(d.getTime() + 7*86400000));
    const goToday  = () => {
      setYear(todayDate.getFullYear()); setMonth(todayDate.getMonth());
      const d = new Date(); d.setDate(d.getDate()-d.getDay()); d.setHours(0,0,0,0); setWeekStart(d);
    };

    const monthLabel = new Date(year,month,1).toLocaleDateString('en-GB',{month:'long',year:'numeric'});
    const weekLabel  = `${weekDays[0].toLocaleDateString('en-GB',{day:'numeric',month:'short'})} – ${weekDays[6].toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}`;

    const btnBase = { background:'none', border:'1px solid #E2E8F0', borderRadius:8, cursor:'pointer', padding:'5px 12px', color:'#64748B', fontFamily:'inherit', fontSize:12 };
    const vTab = active => ({ padding:'5px 12px', borderRadius:7, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:active?700:500, background:active?'#0D1F35':'transparent', color:active?'#fff':'#64748B' });

    // Mini task chip (used in both views)
    function Chip({ task, tall }) {
      const aIds  = parseAssignees(task.assignedTo);
      const color = PRI_COLOR[task.priority] || '#94A3B8';
      const isOD  = isOverdueTask(task);
      const tipNames = aIds.map(id=>TASK_USERS.find(u=>u.id===id)?.fullName).filter(Boolean).join(', ');
      return (
        <div onClick={()=>onTaskClick(task)} title={`${task.title}${tipNames ? ' · ' + tipNames : ''}`}
          style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer',
            padding: tall ? '4px 6px' : '2px 5px',
            borderRadius:4, fontSize:10, fontWeight:600, lineHeight:1.4,
            background: isOD ? '#FEF2F2' : color+'22',
            border: isOD ? '1px solid #FECACA' : '1px solid transparent',
            color: isOD ? '#EF4444' : color,
            overflow:'hidden' }}>
          <span style={{ width:5, height:5, borderRadius:'50%', background:isOD?'#EF4444':color, flexShrink:0 }}/>
          <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.title}</span>
          <div style={{ display:'flex', alignItems:'center', flexShrink:0 }}>
            {aIds.slice(0,2).map((id,i) => {
              const u = TASK_USERS.find(u=>u.id===id);
              return u ? (
                <span key={id} title={u.fullName}
                  style={{ width:13, height:13, borderRadius:'50%', background:u.color, color:'#fff',
                    fontSize:7, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center',
                    flexShrink:0, marginLeft: i>0?-4:0, border: i>0?'1.5px solid #fff':'none' }}>
                  {u.initials[0]}
                </span>
              ) : null;
            })}
            {aIds.length > 2 && <span style={{ fontSize:7, color, fontWeight:700, marginLeft:2 }}>+{aIds.length-2}</span>}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
        {/* Controls row */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexShrink:0 }}>
          <button style={btnBase} onClick={calView==='month'?prevMonth:prevWeek}>‹</button>
          <span style={{ flex:1, textAlign:'center', fontWeight:700, fontSize:14 }}>
            {calView==='month' ? monthLabel : weekLabel}
          </span>
          <button style={btnBase} onClick={calView==='month'?nextMonth:nextWeek}>›</button>
          {(calView==='month' ? !isCurrentMonth : !isCurrentWeek) && (
            <button style={{...btnBase, color:'#0D1F35', borderColor:'#0D1F35', fontWeight:600}} onClick={goToday}>Today</button>
          )}
          <div style={{ display:'inline-flex', background:'#F1F5F9', borderRadius:8, padding:2, gap:1 }}>
            <button style={vTab(calView==='month')} onClick={()=>changeCalView('month')}>Month</button>
            <button style={vTab(calView==='week')}  onClick={()=>changeCalView('week')}>Week</button>
          </div>
        </div>

        {calView === 'month' ? (
          <>
            {/* Day-of-week header */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, flexShrink:0, marginBottom:3 }}>
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
                <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'#94A3B8', paddingBottom:5 }}>{d}</div>
              ))}
            </div>
            {/* Grid */}
            <div style={{ flex:1, overflowY:'auto', display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, alignContent:'start' }}>
              {[...Array(startDow).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)].map((day,i) => {
                const isToday = day && String(day)===String(todayDate.getDate()) && isCurrentMonth;
                const dateKey = day ? `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : null;
                const dayTasks = dateKey ? (byDate[dateKey]||[]) : [];
                return (
                  <div key={i} style={{ minHeight:82,
                    background: day ? (isToday?'#EFF6FF':'#FAFAFA') : 'transparent',
                    border: day ? `1.5px solid ${isToday?'#3B82F6':'#E2E8F0'}` : 'none',
                    borderRadius: day ? 7 : 0, padding: day ? '5px 6px' : 0 }}>
                    {day && <>
                      <div style={{ fontSize:11, fontWeight:isToday?700:500, color:isToday?'#3B82F6':'#64748B', marginBottom:4 }}>{day}</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                        {dayTasks.slice(0,3).map(t=><Chip key={t.id} task={t}/>)}
                        {dayTasks.length>3 && <div style={{ fontSize:9, color:'#94A3B8', fontWeight:600, paddingLeft:4 }}>+{dayTasks.length-3} more</div>}
                      </div>
                    </>}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          /* Week view */
          <div style={{ flex:1, display:'flex', overflow:'hidden', border:'1px solid #E2E8F0', borderRadius:10 }}>
            {weekDays.map((day,i) => {
              const dk       = toDateKey(day);
              const dayTasks = byDate[dk] || [];
              const isToday  = dk === todayKey;
              const isWkend  = day.getDay()===0 || day.getDay()===6;
              return (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0,
                  borderRight: i<6 ? '1px solid #F1F5F9' : 'none',
                  background: isToday ? '#F0F9FF' : isWkend ? '#FAFAFA' : '#fff' }}>
                  {/* Day header */}
                  <div style={{ padding:'8px 5px', textAlign:'center', flexShrink:0,
                    borderBottom:`2px solid ${isToday?'#3B82F6':'#F1F5F9'}`,
                    background: isToday ? '#DBEAFE' : 'transparent' }}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:isToday?'#3B82F6':'#94A3B8' }}>
                      {day.toLocaleDateString('en-GB',{weekday:'short'})}
                    </div>
                    <div style={{ fontSize:17, fontWeight:700, lineHeight:1.2, color:isToday?'#3B82F6':'#475569' }}>
                      {day.getDate()}
                    </div>
                    <div style={{ fontSize:9, color:'#94A3B8', marginTop:1 }}>
                      {day.toLocaleDateString('en-GB',{month:'short'})}
                    </div>
                  </div>
                  {/* Tasks */}
                  <div style={{ flex:1, overflowY:'auto', padding:'6px 5px', display:'flex', flexDirection:'column', gap:4 }}>
                    {dayTasks.length === 0
                      ? <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#E2E8F0', fontSize:18 }}>·</div>
                      : dayTasks.map(t=><Chip key={t.id} task={t} tall={true}/>)
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Priority legend */}
        <div style={{ display:'flex', gap:14, justifyContent:'center', flexShrink:0, paddingTop:10, marginTop:8, borderTop:'1px solid #F1F5F9' }}>
          {[...TASK_PRIS].reverse().map(p=>(
            <div key={p} style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#64748B' }}>
              <span style={{ width:9, height:9, borderRadius:3, background:PRI_COLOR[p]+'44', border:`1.5px solid ${PRI_COLOR[p]}`, flexShrink:0 }}/>
              {p}
            </div>
          ))}
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#64748B' }}>
            <span style={{ width:9, height:9, borderRadius:3, background:'#FEF2F2', border:'1.5px solid #EF4444', flexShrink:0 }}/>
            Overdue
          </div>
        </div>
      </div>
    );
  }

  // ── Task recycle bin ──────────────────────────────────────────────────────────
  function TaskTrashView({ tasks, onRestore, onDeletePermanent, onEmptyTrash }) {
    const [confirmEmpty, setConfirmEmpty] = useState(false);
    return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        <div style={{ padding:'18px 28px', borderBottom:'1px solid var(--line)', background:'var(--bg-elev)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <h1 style={{ fontSize:19, fontWeight:700 }}>Task Recycle Bin</h1>
            <p style={{ fontSize:13, color:'var(--text-subtle)', marginTop:2 }}>{tasks.length===0?'The bin is empty.':`${tasks.length} deleted task${tasks.length!==1?'s':''} - restore or permanently delete.`}</p>
          </div>
          {tasks.length > 0 && (confirmEmpty ? (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:13, color:'#64748B' }}>Delete all permanently?</span>
              <button onClick={()=>{onEmptyTrash();setConfirmEmpty(false);}} style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#EF4444', color:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700 }}>Yes, empty bin</button>
              <button onClick={()=>setConfirmEmpty(false)} style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E2E8F0', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, color:'#64748B' }}>Cancel</button>
            </div>
          ) : (
            <button onClick={()=>setConfirmEmpty(true)} style={{ padding:'9px 18px', borderRadius:8, border:'1px solid #FECACA', background:'#FEF2F2', color:'#EF4444', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600 }}>Empty bin</button>
          ))}
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'20px 28px' }}>
          {tasks.length===0 ? (
            <div style={{ textAlign:'center', padding:'80px 20px' }}>
              <div style={{ fontSize:48, marginBottom:16, opacity:0.15 }}>🗑</div>
              <div style={{ fontSize:15, fontWeight:600, color:'var(--text-subtle)' }}>No deleted tasks</div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {tasks.map(task => {
                const aIds   = parseAssignees(task.assignedTo);
                const aNames = aIds.map(id=>TASK_USERS.find(u=>u.id===id)?.name).filter(Boolean);
                return (
                  <div key={task.id} style={{ background:'#fff', borderRadius:10, padding:'14px 18px', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', gap:14, opacity:0.75 }}>
                    <TaskAvatarGroup assignedTo={task.assignedTo} size={32}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:14, textDecoration:'line-through', color:'#94A3B8', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{task.title}</div>
                      <div style={{ fontSize:12, color:'#94A3B8', marginTop:3, display:'flex', gap:10, alignItems:'center' }}>
                        <span>{aNames.join(', ') || '-'}</span><span>·</span><TaskPriBadge priority={task.priority}/>
                        {task.dueDate && <><span>·</span><span>Due {taskFmtDate(task.dueDate)}</span></>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                      <button onClick={()=>onRestore(task.id)} style={{ padding:'7px 14px', borderRadius:7, border:'1px solid #E2E8F0', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, color:'#0E9384', fontWeight:600 }}>Restore</button>
                      <button onClick={()=>{if(window.confirm(`Permanently delete "${task.title}"?`))onDeletePermanent(task.id);}} style={{ padding:'7px 14px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer', fontFamily:'inherit', fontSize:13, color:'#EF4444', fontWeight:600 }}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Manager dashboard ─────────────────────────────────────────────────────────
  function TaskManagerDashboard({ currentUser, tasks, onAddTask, onUpdateTask, onAddComment, onDeleteTask, onEditTask }) {
    const [view, setView]             = useState(()=>_stickyView);
    const [showCreate, setShowCreate] = useState(false);
    const [selected, setSelected]     = useState(null);
    const [fAssignee, setFAssignee]   = useState(()=>_stickyAssignee);
    const [fPriority, setFPriority]   = useState(()=>_stickyPriority);
    const [showPending,    setShowPending]    = useState(()=>_stickyShowPending);
    const [showInProgress, setShowInProgress] = useState(()=>_stickyShowInProgress);
    const [showCompleted,  setShowCompleted]  = useState(()=>_stickyShowCompleted);
    const [showOverdue,    setShowOverdue]    = useState(()=>_stickyShowOverdue);

    const changeView     = v => { _stickyView=v;     _stickyPersist(); setView(v); };
    const changeAssignee = v => { _stickyAssignee=v; _stickyPersist(); setFAssignee(v); };
    const changePriority = v => { _stickyPriority=v; _stickyPersist(); setFPriority(v); };
    const toggleStatus = key => {
      if (key==='pending')     { const v=!showPending;    _stickyShowPending=v;    setShowPending(v); }
      if (key==='in-progress') { const v=!showInProgress; _stickyShowInProgress=v; setShowInProgress(v); }
      if (key==='completed')   { const v=!showCompleted;  _stickyShowCompleted=v;  setShowCompleted(v); }
      if (key==='overdue')     { const v=!showOverdue;    _stickyShowOverdue=v;    setShowOverdue(v); }
      _stickyPersist();
    };

    const activeStatuses = [
      ...(showPending    ? ['pending']     : []),
      ...(showInProgress ? ['in-progress'] : []),
      ...(showCompleted  ? ['completed']   : []),
    ];

    const stats = {
      total:      tasks.length,
      pending:    tasks.filter(t=>t.status==='pending').length,
      inProgress: tasks.filter(t=>t.status==='in-progress').length,
      completed:  tasks.filter(t=>t.status==='completed').length,
      overdue:    tasks.filter(isOverdueTask).length,
    };

    const matchesFilter = t =>
      (fAssignee==='all' || parseAssignees(t.assignedTo).includes(fAssignee)) &&
      (fPriority==='all' || t.priority===fPriority);

    // Overdue bucket: independent of status toggles - always separated
    const overdueFiltered    = showOverdue ? tasks.filter(t => isOverdueTask(t) && matchesFilter(t)) : [];
    // Non-overdue list: never contains overdue tasks, respects status toggles
    const nonOverdueFiltered = tasks.filter(t => !isOverdueTask(t) && activeStatuses.includes(t.status) && matchesFilter(t));
    // Calendar: overdue tasks included only when the overdue toggle is on
    const calFiltered = tasks.filter(t =>
      (isOverdueTask(t) ? showOverdue : activeStatuses.includes(t.status)) && matchesFilter(t)
    );
    const priOrd = ['Critical','High','Medium','Low'];

    const tabStyle = active => ({ padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:active?700:500, background:active?'#0D1F35':'transparent', color:active?'#fff':'#64748B', transition:'all 0.15s' });
    const selStyle = { border:'1px solid #E2E8F0', borderRadius:8, padding:'7px 10px', fontSize:13, fontFamily:'inherit', background:'#fff', color:'var(--text)', cursor:'pointer', outline:'none' };

    const detailModal = selected && (
      <TaskDetailModal task={selected} currentUser={currentUser}
        onUpdate={(updated,event)=>{onUpdateTask(updated,event);setSelected(updated);}}
        onAddComment={onAddComment}
        onDelete={id=>{onDeleteTask(id);setSelected(null);}}
        onEdit={onEditTask ? async(id,updates)=>{const saved=await onEditTask(id,updates);setSelected(saved);return saved;} : undefined}
        onClose={()=>setSelected(null)}/>
    );

    return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        <div style={{ padding:'18px 28px', borderBottom:'1px solid var(--line)', background:'var(--bg-elev)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexShrink:0 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <h1 style={{ fontSize:19, fontWeight:700 }}>Task Overview</h1>
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', padding:'3px 8px', borderRadius:4, background:'oklch(94% 0.04 264)', color:'oklch(40% 0.12 264)' }}>Manager View</span>
            </div>
            <p style={{ fontSize:13, color:'var(--text-subtle)', marginTop:2 }}>Manage and track tasks across the IT team</p>
          </div>
          <button onClick={()=>setShowCreate(true)} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px', borderRadius:10, border:'none', background:'#0D1F35', color:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700 }}>
            <span style={{ fontSize:18, lineHeight:1, marginTop:-1 }}>+</span> New Task
          </button>
        </div>

        {/* Stats */}
        <div className="tasks-stats" style={{ padding:'16px 28px 0', display:'flex', gap:12, flexShrink:0 }}>
          <TaskStatCard label="Total"       value={stats.total}/>
          <TaskStatCard label="Pending"     value={stats.pending}    accent="#64748B"/>
          <TaskStatCard label="In Progress" value={stats.inProgress} accent="#3B82F6"/>
          <TaskStatCard label="Completed"   value={stats.completed}  accent="#22C55E"/>
          <TaskStatCard label="Overdue"     value={stats.overdue}    accent={stats.overdue>0?'#EF4444':undefined}/>
        </div>

        {/* Controls */}
        <div style={{ padding:'14px 28px', flexShrink:0, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          <div style={{ display:'inline-flex', background:'#F1F5F9', borderRadius:10, padding:3, gap:2 }}>
            <button style={tabStyle(view==='board')}    onClick={()=>changeView('board')}>Board</button>
            <button style={tabStyle(view==='list')}     onClick={()=>changeView('list')}>List</button>
            <button style={tabStyle(view==='calendar')} onClick={()=>changeView('calendar')}>Calendar</button>
          </div>
          <div style={{ width:1, height:22, background:'#E2E8F0', flexShrink:0 }}/>
          <StatusToggles showPending={showPending} showInProgress={showInProgress} showCompleted={showCompleted} showOverdue={showOverdue} onToggle={toggleStatus}/>
          {(view==='list'||view==='calendar') && (
            <div style={{ display:'flex', gap:8, marginLeft:'auto' }}>
              <select style={selStyle} value={fAssignee} onChange={e=>changeAssignee(e.target.value)}>
                <option value="all">All Members</option>
                {TASK_USERS.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select style={selStyle} value={fPriority} onChange={e=>changePriority(e.target.value)}>
                <option value="all">All Priorities</option>
                {TASK_PRIS.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="tasks-content" style={{ flex:1, overflow:'hidden', padding:'0 28px 28px', display:'flex', flexDirection:'column' }}>
          {view==='board' ? (
            <div className="tasks-board" style={{ display:'flex', gap:14, alignItems:'flex-start', height:'100%', overflowX:'auto', overflowY:'auto', paddingBottom:8 }}>
              {TASK_USERS.map(u=>(
                <BoardColumn key={u.id} user={u}
                  tasks={tasks.filter(t => parseAssignees(t.assignedTo).includes(u.id) && (
                    isOverdueTask(t) ? showOverdue : activeStatuses.includes(t.status)
                  ))}
                  onTaskClick={t=>setSelected(t)} onDeleteTask={onDeleteTask} currentUser={currentUser}/>
              ))}
            </div>
          ) : view==='list' ? (
            <div style={{ flex:1, overflowY:'auto' }}>
              {/* Overdue group */}
              {showOverdue && overdueFiltered.length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:'#EF4444' }}/>
                    <span style={{ fontSize:12, fontWeight:700, color:'#EF4444', textTransform:'uppercase', letterSpacing:'0.04em' }}>Overdue</span>
                    <span style={{ fontSize:11, background:'#FEF2F2', color:'#EF4444', padding:'1px 8px', borderRadius:20, fontWeight:700 }}>{overdueFiltered.length}</span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {overdueFiltered.sort((a,b)=>{ if(!a.dueDate&&!b.dueDate)return 0; if(!a.dueDate)return 1; if(!b.dueDate)return -1; return new Date(a.dueDate)-new Date(b.dueDate); })
                      .map(t=><TaskCard key={t.id} task={t} onClick={task=>setSelected(task)} onDelete={onDeleteTask} currentUser={currentUser}/>)}
                  </div>
                </div>
              )}
              {/* Normal tasks */}
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {nonOverdueFiltered.length===0 && overdueFiltered.length===0
                  ? <div style={{ textAlign:'center', padding:'48px 20px', color:'var(--text-subtle)', fontSize:14 }}>No tasks match the selected filters.</div>
                  : nonOverdueFiltered
                      .sort((a,b)=>{ if(!a.dueDate&&!b.dueDate)return 0; if(!a.dueDate)return 1; if(!b.dueDate)return -1; return new Date(a.dueDate)-new Date(b.dueDate); })
                      .map(t=><TaskCard key={t.id} task={t} onClick={task=>setSelected(task)} onDelete={onDeleteTask} currentUser={currentUser}/>)
                }
              </div>
            </div>
          ) : (
            <TaskCalendar tasks={calFiltered} onTaskClick={t=>setSelected(t)}/>
          )}
        </div>

        {showCreate && <CreateTaskModal currentUser={currentUser} onSave={async task=>{await onAddTask(task);setShowCreate(false);}} onClose={()=>setShowCreate(false)}/>}
        {detailModal}
      </div>
    );
  }

  // ── Member dashboard ──────────────────────────────────────────────────────────
  function TaskMemberDashboard({ currentUser, tasks, onUpdateTask, onAddComment, onAddTask, onDeleteTask, onEditTask }) {
    const [memberView, setMemberView] = useState(()=>_stickyMemberView);
    const [selected, setSelected]     = useState(null);
    const [showCreate, setShowCreate] = useState(false);

    // Sidebar "+ New task" shortcut - sidebar sets window._triagePendingNewTask
    // before navigating here. We pop it on mount and open the create modal.
    // Cleared immediately so re-navigating to /tasks-mine doesn't keep
    // reopening the modal.
    React.useEffect(() => {
      if (window._triagePendingNewTask) {
        window._triagePendingNewTask = false;
        setShowCreate(true);
      }
    }, []);
    const [showPending,    setShowPending]    = useState(()=>_stickyShowPending);
    const [showInProgress, setShowInProgress] = useState(()=>_stickyShowInProgress);
    const [showCompleted,  setShowCompleted]  = useState(()=>_stickyShowCompleted);
    const [showOverdue,    setShowOverdue]    = useState(()=>_stickyShowOverdue);

    const changeMemberView = v => { _stickyMemberView=v; _stickyPersist(); setMemberView(v); };
    const toggleStatus = key => {
      if (key==='pending')     { const v=!showPending;    _stickyShowPending=v;    setShowPending(v); }
      if (key==='in-progress') { const v=!showInProgress; _stickyShowInProgress=v; setShowInProgress(v); }
      if (key==='completed')   { const v=!showCompleted;  _stickyShowCompleted=v;  setShowCompleted(v); }
      if (key==='overdue')     { const v=!showOverdue;    _stickyShowOverdue=v;    setShowOverdue(v); }
      _stickyPersist();
    };

    const mine       = tasks.filter(t=>parseAssignees(t.assignedTo).includes(currentUser.id));
    // Overdue tasks are always separated from status columns so the counts never overlap
    const allOverdue = mine.filter(isOverdueTask);
    const overdueCnt = allOverdue.length;
    const pending    = mine.filter(t=>t.status==='pending'     && !isOverdueTask(t));
    const inProgress = mine.filter(t=>t.status==='in-progress' && !isOverdueTask(t));
    const completed  = mine.filter(t=>t.status==='completed');

    const priSort = (a,b) => { const ord=['Critical','High','Medium','Low']; return ord.indexOf(a.priority)-ord.indexOf(b.priority); };

    const columns = [
      ...(showOverdue ? [{ key:'overdue', label:'Overdue', color:'#EF4444', bg:'#FEF2F2', tasks:allOverdue.slice().sort(priSort) }] : []),
      ...(showPending    ? [{ key:'pending',     label:'Pending',     color:'#64748B', bg:'#F1F5F9', tasks:pending.slice().sort(priSort) }] : []),
      ...(showInProgress ? [{ key:'in-progress', label:'In Progress', color:'#3B82F6', bg:'#EFF6FF', tasks:inProgress.slice().sort(priSort) }] : []),
      ...(showCompleted  ? [{ key:'completed',   label:'Completed',   color:'#22C55E', bg:'#F0FDF4', tasks:completed.slice().sort(priSort) }] : []),
    ];

    // Calendar: show tasks filtered by active statuses
    const activeStatuses = [
      ...(showPending    ? ['pending']     : []),
      ...(showInProgress ? ['in-progress'] : []),
      ...(showCompleted  ? ['completed']   : []),
    ];
    const calendarTasks = mine.filter(t =>
      isOverdueTask(t) ? showOverdue : activeStatuses.includes(t.status)
    );

    const tabStyle = active => ({ padding:'6px 14px', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:active?700:500, background:active?'#0D1F35':'transparent', color:active?'#fff':'#64748B', transition:'all 0.15s' });

    const detailModal = selected && (
      <TaskDetailModal task={selected} currentUser={currentUser}
        onUpdate={(updated,event)=>{onUpdateTask(updated,event);setSelected(updated);}}
        onAddComment={onAddComment}
        onDelete={id=>{onDeleteTask(id);setSelected(null);}}
        onEdit={onEditTask ? async(id,updates)=>{const saved=await onEditTask(id,updates);setSelected(saved);return saved;} : undefined}
        onClose={()=>setSelected(null)}/>
    );

    return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        {/* Compact header */}
        <div className="tasks-header" style={{ padding:'13px 28px', borderBottom:'1px solid var(--line)', background:'var(--bg-elev)', flexShrink:0 }}>
          <div className="tasks-header-row" style={{ display:'flex', alignItems:'center', gap:12 }}>
            <TaskAvatar userId={currentUser.id} size={34}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:14, lineHeight:1.2 }}>{currentUser.fullName}</div>
              <div style={{ fontSize:12, color:'var(--text-subtle)' }}>{currentUser.role}</div>
            </div>
            {/* Stat chips */}
            <div className="tasks-header-chips" style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
              {overdueCnt > 0 && (
                <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'#FEF2F2', color: showOverdue?'#EF4444':'#CBD5E1' }}>
                  {overdueCnt} Overdue
                </span>
              )}
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:'#F1F5F9', color: showPending?'#64748B':'#CBD5E1' }}>{pending.length} Pending</span>
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:'#EFF6FF', color: showInProgress?'#3B82F6':'#CBD5E1' }}>{inProgress.length} In Progress</span>
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:'#F0FDF4', color: showCompleted?'#22C55E':'#CBD5E1' }}>{completed.length} Done</span>
            </div>
            {onAddTask && (
              <button onClick={()=>setShowCreate(true)} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:9, border:'none', background:'#0D1F35', color:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, flexShrink:0 }}>
                <span style={{ fontSize:16, lineHeight:1 }}>+</span> New Task
              </button>
            )}
          </div>
          <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ display:'inline-flex', background:'#F1F5F9', borderRadius:8, padding:3, gap:2 }}>
              <button style={tabStyle(memberView==='kanban')}   onClick={()=>changeMemberView('kanban')}>Kanban</button>
              <button style={tabStyle(memberView==='calendar')} onClick={()=>changeMemberView('calendar')}>Calendar</button>
            </div>
            <div style={{ width:1, height:20, background:'#E2E8F0', flexShrink:0 }}/>
            <StatusToggles showPending={showPending} showInProgress={showInProgress} showCompleted={showCompleted} showOverdue={showOverdue} onToggle={toggleStatus}/>
          </div>
        </div>

        {/* Content */}
        {memberView==='calendar' ? (
          <div style={{ flex:1, overflow:'hidden', padding:'20px 28px', display:'flex', flexDirection:'column' }}>
            <TaskCalendar tasks={calendarTasks} onTaskClick={t=>setSelected(t)}/>
          </div>
        ) : columns.length===0 ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8, color:'var(--text-subtle)' }}>
            <div style={{ fontSize:28, opacity:0.2 }}>✓</div>
            <div style={{ fontSize:14 }}>No status filters active - use the toggles above.</div>
          </div>
        ) : (
          <div className="tasks-kanban" style={{ flex:1, display:'flex', overflow:'hidden', padding:'16px 20px 20px', gap:0 }}>
            {columns.map((col,i) => (
              <div key={col.key} className="tasks-kanban-col" style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0,
                borderRight: i<columns.length-1 ? '1px solid var(--line)' : 'none',
                paddingRight: i<columns.length-1 ? 16 : 0,
                paddingLeft: i>0 ? 16 : 0 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, flexShrink:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <span style={{ width:7, height:7, borderRadius:'50%', background:col.color, flexShrink:0 }}/>
                    <span style={{ fontWeight:700, fontSize:12, color:col.color, letterSpacing:'0.03em' }}>{col.label}</span>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, padding:'1px 7px', borderRadius:20, background:col.bg, color:col.color }}>{col.tasks.length}</span>
                </div>
                <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:7 }}>
                  {col.tasks.length===0
                    ? <div style={{ background:'#F8FAFC', border:'1.5px dashed #E2E8F0', borderRadius:9, padding:'18px 12px', textAlign:'center', color:'#94A3B8', fontSize:12 }}>No {col.label.toLowerCase()} tasks</div>
                    : col.tasks.map(t=><TaskCard key={t.id} task={t} onClick={task=>setSelected(task)} onDelete={onDeleteTask} compact={true}/>)
                  }
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreate && <CreateTaskModal currentUser={currentUser} onSave={async task=>{await onAddTask(task);setShowCreate(false);}} onClose={()=>setShowCreate(false)}/>}
        {detailModal}
      </div>
    );
  }

  // ── Phone-first Tasks view ────────────────────────────────────────────────────
  // Purpose-built for phones (≤600px): a single scrolling column of tasks grouped
  // by status, tap a card to open the detail sheet (view / edit / complete / comment).
  // Replaces the squeezed desktop kanban on mobile only. Reuses TaskCard, the
  // TaskDetailModal + CreateTaskModal bottom sheets, and TasksView's handlers.
  function MobileTasksDashboard({ currentUser, tasks, myTasksOnly, onAddTask, onUpdateTask, onAddComment, onDeleteTask, onEditTask }) {
    const [scope, setScope]           = useState(myTasksOnly ? 'mine' : 'team');
    const [showCreate, setShowCreate] = useState(false);
    const [openId, setOpenId]         = useState(null);
    const [hidden, setHidden]         = useState({});

    const mine   = t => parseAssignees(t.assignedTo).includes(currentUser.id);
    const scoped = scope === 'mine' ? tasks.filter(mine) : tasks;

    const GROUPS = [
      { key:'overdue',     label:'Overdue',     color:'#EF4444', match: t => isOverdueTask(t) },
      { key:'pending',     label:'Pending',     color:'#64748B', match: t => !isOverdueTask(t) && t.status === 'pending' },
      { key:'in-progress', label:'In Progress', color:'#3B82F6', match: t => !isOverdueTask(t) && t.status === 'in-progress' },
      { key:'completed',   label:'Completed',   color:'#22C55E', match: t => !isOverdueTask(t) && t.status === 'completed' },
    ];
    const open = openId ? tasks.find(t => t.id === openId) : null;
    const scopeBtn = active => ({ padding:'7px 15px', borderRadius:7, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
      background: active ? '#0D1F35' : 'transparent', color: active ? '#fff' : '#64748B' });
    const anyShown = GROUPS.some(g => !hidden[g.key] && scoped.some(g.match));

    return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'var(--bg)' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--line)', background:'var(--bg-elev)', display:'flex', flexDirection:'column', gap:10, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ display:'inline-flex', background:'#F1F5F9', borderRadius:9, padding:3, gap:2 }}>
              <button onClick={() => setScope('mine')} style={scopeBtn(scope === 'mine')}>My tasks</button>
              <button onClick={() => setScope('team')} style={scopeBtn(scope === 'team')}>Team</button>
            </div>
            <button onClick={() => setShowCreate(true)}
              style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6, padding:'9px 15px', borderRadius:9, border:'none', background:'#0D1F35', color:'#fff', fontWeight:700, fontSize:14, fontFamily:'inherit', cursor:'pointer' }}>
              <span style={{ fontSize:17, lineHeight:1, marginTop:-1 }}>+</span> New
            </button>
          </div>
          <div style={{ display:'flex', gap:6, overflowX:'auto', WebkitOverflowScrolling:'touch', paddingBottom:2 }}>
            {GROUPS.map(g => {
              const count = scoped.filter(g.match).length;
              const off = !!hidden[g.key];
              return (
                <button key={g.key} onClick={() => setHidden(h => ({ ...h, [g.key]: !h[g.key] }))}
                  style={{ flexShrink:0, display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:999,
                    border:`1px solid ${off ? 'var(--line)' : g.color}`, background: off ? 'transparent' : g.color + '18',
                    color: off ? 'var(--text-subtle)' : g.color, fontWeight:600, fontSize:13, fontFamily:'inherit', cursor:'pointer', whiteSpace:'nowrap' }}>
                  <span style={{ width:7, height:7, borderRadius:99, background:g.color, opacity: off ? 0.35 : 1 }}/>
                  {g.label} <span style={{ opacity:0.7 }}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch', padding:'12px 12px 40px' }}>
          {!anyShown && (
            <div style={{ textAlign:'center', color:'var(--text-subtle)', fontSize:14, padding:'56px 20px' }}>
              {scope === 'mine' ? 'You have no tasks here.' : 'No tasks to show.'}
            </div>
          )}
          {GROUPS.map(g => {
            if (hidden[g.key]) return null;
            const list = scoped.filter(g.match);
            if (list.length === 0) return null;
            return (
              <div key={g.key} style={{ marginBottom:18 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, margin:'0 4px 8px' }}>
                  <span style={{ width:8, height:8, borderRadius:99, background:g.color }}/>
                  <span style={{ fontWeight:700, fontSize:13, color:'var(--text)' }}>{g.label}</span>
                  <span style={{ fontSize:12, color:'var(--text-subtle)' }}>{list.length}</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {list.map(t => (
                    <TaskCard key={t.id} task={t} currentUser={currentUser}
                      onClick={() => setOpenId(t.id)} onDelete={onDeleteTask}/>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {open && (
          <TaskDetailModal task={open} currentUser={currentUser}
            onUpdate={onUpdateTask} onAddComment={onAddComment}
            onDelete={id => { onDeleteTask(id); setOpenId(null); }}
            onEdit={onEditTask} onClose={() => setOpenId(null)}/>
        )}
        {showCreate && (
          <CreateTaskModal currentUser={currentUser}
            onSave={async t => { await onAddTask(t); setShowCreate(false); }}
            onClose={() => setShowCreate(false)}/>
        )}
      </div>
    );
  }

  // ── Main view ─────────────────────────────────────────────────────────────────
  const TasksView = ({ myTasksOnly = false, trashView = false }) => {
    // No fallback to TASK_MANAGERS[0] here: that made any IT-group member
    // NOT in the hardcoded TASK_USERS list (new hire, service account)
    // silently act AS JAMES - their comments, task creations and manager
    // powers all attributed to him. An unmapped user now gets an explicit
    // guest identity with no manager rights; their actions carry their
    // own slug so nothing is misattributed.
    const taskUser = TASK_USERS.find(u => u.id === ME_ID) || {
      id: ME_ID || 'unknown',
      name: ME_ID || 'Unknown',
      fullName: ME_ID || 'Unknown user',
      role: 'IT',
      isManager: false,
      initials: (ME_ID || '?').slice(0, 2).toUpperCase(),
      color: '#6B7280',
    };
    const [tasks,   setTasks]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [toasts,  setToasts]  = useState([]);
    const toastId = useRef(0);
    const isPhone = useTaskIsPhone();

    // Lightweight reloader used by initial load + focus refresh + the
    // triage:tasks-need-refresh event. Doesn't repeat the localStorage
    // migration path (that only runs once at first load).
    const reloadTasks = React.useCallback(() => {
      return taskApi('GET','/tasks')
        .then(data => { if (Array.isArray(data)) setTasks(data); })
        .catch(e => { console.warn('[tasks] reload failed:', e.message); });
    }, []);

    useEffect(() => {
      taskApi('GET','/tasks').then(async data => {
        if (data && data.length > 0) { setTasks(data); return; }
        try {
          const raw = localStorage.getItem('lt_it_tasks_v2');
          if (raw) {
            const localTasks = JSON.parse(raw);
            if (localTasks.length > 0) {
              const migrated = [];
              for (const t of localTasks) {
                try {
                  const saved = await taskApi('POST','/tasks',{ title:t.title, description:t.description||'', category:t.category||TASK_CATS[0], priority:t.priority||'Medium', status:t.status||'pending', assignedTo:t.assignedTo, assignedBy:t.assignedBy||taskUser.id, dueDate:t.dueDate||null });
                  for (const c of (t.comments||[])) { try { await taskApi('POST',`/tasks/${saved.id}/comments`,{authorId:c.authorId,text:c.text}); } catch(_){} }
                  migrated.push(saved);
                } catch(_) {}
              }
              if (migrated.length) { localStorage.removeItem('lt_it_tasks_v2'); const fresh=await taskApi('GET','/tasks'); setTasks(fresh||[]); return; }
            }
          }
        } catch(_) {}
        setTasks(data||[]);
      }).catch(e => { console.error(e); addToast('info','Load error','Could not load tasks from server.'); })
        .finally(() => setLoading(false));
    }, []);

    // Refetch tasks when:
    //   1. The tab regains focus (catches changes another user made while
    //      you were away)
    //   2. The window's online status flips back to true
    //   3. Anything in the app dispatches triage:tasks-need-refresh
    //   4. Every 30 seconds while the tab is visible (covers the "I'm
    //      staring at the screen and just got assigned a task" case)
    useEffect(() => {
      const onFocus = () => { if (!document.hidden) reloadTasks(); };
      window.addEventListener('focus', onFocus);
      window.addEventListener('online', onFocus);
      document.addEventListener('visibilitychange', onFocus);
      window.addEventListener('triage:tasks-need-refresh', reloadTasks);

      // Background polling. Skipped when the tab is hidden so we don't burn
      // bandwidth in inactive tabs. 30s is the usual sweet spot for "feels
      // live without hammering the API".
      const poll = setInterval(() => { if (!document.hidden) reloadTasks(); }, 30000);

      return () => {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('online', onFocus);
        document.removeEventListener('visibilitychange', onFocus);
        window.removeEventListener('triage:tasks-need-refresh', reloadTasks);
        clearInterval(poll);
      };
    }, [reloadTasks]);

    function addToast(type,title,message) {
      const id = ++toastId.current;
      setToasts(p=>[...p,{id,type,title,message}]);
      setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),5000);
    }

    async function handleAddTask(task) {
      try {
        const saved = await taskApi('POST','/tasks',{ title:task.title, description:task.description||'', category:task.category, priority:task.priority, status:'pending', assignedTo:task.assignedTo, assignedBy:task.assignedBy||taskUser.id, dueDate:task.dueDate||null, convertToTicketOnComplete:!!task.convertToTicketOnComplete });
        setTasks(p=>[saved,...p]);
        const aIds   = parseAssignees(saved.assignedTo);
        const aNames = aIds.map(id=>TASK_USERS.find(u=>u.id===id)?.name).filter(Boolean);
        addToast('assign','Task created', aIds.length===1 && aIds[0]===taskUser.id ? `"${saved.title}" assigned to yourself` : `"${saved.title}" → ${aNames.join(', ')}`);
      } catch(e) { addToast('info','Error','Could not create task.'); throw e; }
    }

    async function handleUpdateTask(updated,event) {
      try {
        const saved = await taskApi('PATCH',`/tasks/${updated.id}`,{status:updated.status});
        setTasks(p=>p.map(t=>t.id===saved.id?saved:t));
        if (event==='completed') addToast('complete','Task completed',`"${saved.title}"`);
        // The server just auto-created a closed ticket if the task had
        // convertToTicketOnComplete. Tell app.jsx to reload tickets so
        // the new one shows in the Closed list without a manual refresh.
        if (event === 'completed' && saved.convertToTicketOnComplete) {
          window.dispatchEvent(new CustomEvent('triage:tickets-need-refresh'));
        }
      } catch(e) { addToast('info','Error','Could not update task.'); }
    }

    async function handleEditTask(id,updates) {
      try {
        // Snapshot the previous status so we can detect transition to
        // completed and trigger a tickets reload (same reason as in
        // handleUpdateTask above).
        const before = tasks.find(t => t.id === id);
        const saved = await taskApi('PATCH',`/tasks/${id}`,{ title:updates.title, description:updates.description, category:updates.category, priority:updates.priority, assignedTo:updates.assignedTo, dueDate:updates.dueDate||null, status:updates.status, convertToTicketOnComplete:!!updates.convertToTicketOnComplete });
        setTasks(p=>p.map(t=>t.id===saved.id?saved:t));
        addToast('assign','Task updated',`"${saved.title}" updated successfully.`);
        if (saved.status === 'completed' && before?.status !== 'completed' && saved.convertToTicketOnComplete) {
          window.dispatchEvent(new CustomEvent('triage:tickets-need-refresh'));
        }
        return saved;
      } catch(e) { addToast('info','Error','Could not update task.'); throw e; }
    }

    async function handleAddComment(taskId,comment) {
      try {
        const saved = await taskApi('POST',`/tasks/${taskId}/comments`,comment);
        setTasks(p=>p.map(t=>t.id===taskId?{...t,comments:[...(t.comments||[]),saved]}:t));
        return saved;
      } catch(e) { addToast('info','Error','Could not post comment.'); return null; }
    }

    async function handleDeleteTask(id) {
      try {
        await taskApi('PATCH',`/tasks/${id}`,{trashed:true});
        setTasks(p=>p.map(t=>t.id===id?{...t,trashed:true}:t));
        addToast('delete','Moved to bin','Task moved to the recycle bin.');
      } catch(e) { addToast('info','Error','Could not delete task.'); }
    }

    async function handleRestoreTask(id) {
      try {
        const saved = await taskApi('PATCH',`/tasks/${id}`,{trashed:false});
        setTasks(p=>p.map(t=>t.id===id?saved:t));
        addToast('assign','Restored','Task restored successfully.');
      } catch(e) { addToast('info','Error','Could not restore task.'); }
    }

    async function handleDeletePermanent(id) {
      try { await taskApi('DELETE',`/tasks/${id}`); setTasks(p=>p.filter(t=>t.id!==id)); }
      catch(e) { addToast('info','Error','Could not permanently delete task.'); }
    }

    async function handleEmptyTrash() {
      try {
        await taskApi('DELETE','/tasks/empty-trash');
        setTasks(p=>p.filter(t=>!t.trashed));
        addToast('delete','Bin emptied','All trashed tasks permanently deleted.');
      } catch(e) { addToast('info','Error','Could not empty the bin.'); }
    }

    const activeTasks  = tasks.filter(t=>!t.trashed);
    const trashedTasks = tasks.filter(t=>t.trashed);

    if (loading) return (
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:'var(--text-subtle)' }}>
        <div style={{ fontSize:28, opacity:0.25 }}>✓</div>
        <div style={{ fontSize:14 }}>Loading tasks…</div>
      </div>
    );

    return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        {trashView ? (
          <TaskTrashView tasks={trashedTasks} onRestore={handleRestoreTask} onDeletePermanent={handleDeletePermanent} onEmptyTrash={handleEmptyTrash}/>
        ) : isPhone ? (
          // Phones get the purpose-built single-column view; desktop/tablet keep
          // the kanban dashboards below unchanged.
          <MobileTasksDashboard currentUser={taskUser} tasks={activeTasks} myTasksOnly={myTasksOnly}
            onAddTask={handleAddTask} onUpdateTask={handleUpdateTask} onAddComment={handleAddComment}
            onDeleteTask={handleDeleteTask} onEditTask={handleEditTask}/>
        ) : (!myTasksOnly) ? (
          // Manager view shown to ALL users on /tasks-board (the "To-Do List"
          // sidebar entry). Previously gated to isManager but the team
          // wanted everyone to see everyone's tasks. /tasks (the "My Tasks"
          // sidebar entry, myTasksOnly=true) still shows just the user's own.
          <TaskManagerDashboard currentUser={taskUser} tasks={activeTasks}
            onAddTask={handleAddTask} onUpdateTask={handleUpdateTask}
            onAddComment={handleAddComment} onDeleteTask={handleDeleteTask} onEditTask={handleEditTask}/>
        ) : (
          <TaskMemberDashboard currentUser={taskUser} tasks={activeTasks}
            onUpdateTask={handleUpdateTask} onAddComment={handleAddComment}
            onAddTask={handleAddTask} onDeleteTask={handleDeleteTask} onEditTask={handleEditTask}/>
        )}
        <TaskToast toasts={toasts} onDismiss={id=>setToasts(p=>p.filter(t=>t.id!==id))}/>
      </div>
    );
  };

export { TasksView };